import { defineQuiz } from "./_schema";
import type { Answers, PersonaKey, Question } from "./_schema";
import { deriveLibidoProfile } from "./quiz-libido-profile";
import { MM_0102 } from "./_experiments";

/**
 * LIBIDO / "MANOPAUSE" QUIZ -- cold-traffic destination for the bedroom angle.
 *
 * Promise: "has something changed in the bedroom?" -- the flow opens on the
 * symptom the ad sells and never asks the user to talk about anything else
 * first. That is the constraint the whole build inherits: no sleep question, no
 * stress question, no diet question, so the verdict has to be reachable from
 * sexual-function answers alone (see quizzes/quiz-libido-profile.ts).
 *
 * Mechanism: the flow's ten screens are, almost exactly, the EMAS late-onset
 * hypogonadism instrument -- low libido (S1), loss of morning erections (S2),
 * unreliable erections (S4) -- and the research doc records that it is the
 * CLUSTERING of those three that predicts measured low T. Bridge 2 names the
 * pattern "manopause"; the verdict names the mechanism underneath it
 * (BLOCKED TESTOSTERONE -- rising SHBG binding free T even when total T holds).
 *
 * 10 single-choice screens + 3 interstitials (2 bridges + analyzing) = 13.
 * Design spec: docs/specs/2026-08-21-quiz-libido-quiz-design.md.
 *
 * SOURCING. No performance ads or LPs were available at design time -- the
 * operator supplied a finished copy document and directed that the ten
 * questions ship as written, with no added aspiration beat and no added
 * tolerance check. Consequences, recorded so a later reader does not
 * "discover" them as bugs:
 *   - The flow has NO positive/aspiration screen. It opens negative on S1 and
 *     stays neutral-to-negative to the end, which inverts the emotional
 *     journey's opening beat. Watch Q1 -> completion drop-off against quiz-v6;
 *     if it reads worse, an aspiration screen at S1 (the quiz-sleep
 *     `sleep-goal` pattern) is the first thing to try.
 *   - There is no dedicated tolerance check, so `confidence` (S9) doubles as
 *     one -- it is the only screen that asks what the pattern COSTS, and it is
 *     what sets the quality-of-life floor in the deriver.
 * If ads later show a fixed sign-panel order that disagrees with this
 * sequence, the ad wins.
 *
 * OPTION ORDER. The source document ordered five screens worst-answer-first
 * (S1, S6, S7, S8, S9) and three best-answer-first (S2, S4, S5). All three have
 * been normalised to worst-first here, matching the document's own majority and
 * every other quiz in the repo. A ten-screen instrument that flips severity
 * direction three times invites a habit-tap artifact in exactly the data the
 * quiz exists to collect -- and severity direction is presentation, not the
 * deriver contract (values are). Labels are otherwise verbatim.
 *
 * Like quiz-v6 and cortisol, this quiz has no GLP-1 screener, so `glp1-user` is
 * unreachable from the classifier. `performance` is unreachable too: it needs a
 * lean body-composition signal, and this flow's body question offers no lean
 * option (deliberate -- see BODY_TO_IGNITE in the profile file).
 */

const QUESTION_ORDER = [
  "libido",
  "morning-wood",
  "age",
  "reliability",
  "energy-day",
  "info-absolution",
  "body-change",
  "sex-thoughts",
  "pill-reliance",
  "confidence",
  "when-started",
  "info-mechanism",
  "info-analyzing",
] as const;

const questions: Question[] = [
  {
    // S1 carries the QuizIntro block instead of the progress bar (rule 19),
    // which makes it the fold-critical screen. Unlike quiz-v6 / cortisol /
    // quiz-sleep -- all of which put the one-line `age` question here for fold
    // headroom -- this quiz opens on the symptom, because a cold click from a
    // bedroom ad has to land on the bedroom. This prompt therefore renders as
    // THREE lines of Martian Mono (101px) where those quizzes render one, and
    // the headroom is bought back twice on the intro side: a 37-character H1
    // (two lines, vs quiz-sleep's 66-character four) and a one-line introBody.
    //
    // Measured over CDP at 375x667 / DPR 2: H1 48-130, introBody 150-176,
    // testimonial 200-354, prompt 378-479, pill 1 503-559, pill 2 571-627.
    // The second answer pill therefore clears the iPhone SE Safari URL-bar fold
    // (~628) and matches quiz-v6's 630px reference. The budget is spent: at two
    // lines the introBody put pill 2 at 653, and a fourth prompt line would add
    // ~34px. Re-measure if any of the three is lengthened -- do not assume.
    id: "libido",
    type: "single",
    prompt: "Compared to a few years ago, how would you describe your sex drive?",
    options: [
      { value: "significantly-lower", label: "It’s significantly lower" },
      { value: "somewhat-lower", label: "It’s somewhat lower" },
      { value: "about-same", label: "About the same" },
      { value: "not-sure", label: "I’m not sure" },
    ],
  },
  {
    // EMAS symptom 3 of 3. Source document listed these best-first; normalised
    // to worst-first (see the OPTION ORDER note above).
    id: "morning-wood",
    type: "single",
    prompt: "When was the last time you woke up with strong morning wood?",
    options: [
      { value: "cant-remember", label: "I can’t remember" },
      { value: "rarely", label: "Rarely" },
      { value: "few-times-week", label: "A few times a week" },
      { value: "almost-every-morning", label: "Almost every morning" },
    ],
  },
  {
    // Buckets split at 35 rather than quiz-v6's 30 -- the angle's audience
    // boundary, kept as authored. Translated onto quiz-v6's coarser buckets in
    // the deriver (AGE_TO_IGNITE), so the label the user saw is what lands in
    // quiz_events while the deriver still reads a value it understands.
    id: "age",
    type: "single",
    prompt: "How old are you?",
    options: [
      { value: "50-plus", label: "50+ years old" },
      { value: "40-49", label: "40–49 years old" },
      { value: "35-39", label: "35–39 years old" },
      { value: "under-35", label: "Under 35" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Your libido isn’t just about what’s happening in the bedroom. Changes in testosterone as you age can affect sexual desire, energy, mood, and performance… which is why a fading sex drive can be one of the first signs that something has changed.",
    },
  },
  {
    // EMAS symptom 2 of 3 (erectile difficulty). Normalised to worst-first.
    id: "reliability",
    type: "single",
    prompt: "When you want to have sex, how often does your body cooperate the way you want it to?",
    options: [
      { value: "much-less-often", label: "Much less often than it used to" },
      { value: "hit-or-miss", label: "It’s become hit or miss" },
      { value: "usually", label: "Usually, but not always" },
      { value: "almost-always", label: "Almost always" },
    ],
  },
  {
    // Own id rather than quiz-v6's `energy-3pm`: the middle rungs are not
    // quiz-v6's ("tired more often" is a whole-day complaint, quiz-v6's
    // `afternoon-slump` is a 3pm one). Positionally translated in the deriver
    // (ENERGY_TO_IGNITE) so it still feeds severity and QoL organically.
    // Normalised to worst-first.
    id: "energy-day",
    type: "single",
    prompt: "How would you describe your energy during the day?",
    options: [
      { value: "running-empty", label: "I feel like I’m running on empty" },
      { value: "tired-often", label: "I’m tired more often than I used to be" },
      { value: "afternoon-slump", label: "I hit an afternoon slump" },
      { value: "plenty", label: "I have plenty of energy" },
    ],
  },
  {
    // Absolution bridge. Sits after the five sexual/energy admissions and
    // before the body/mind half of the flow, which is the beat it exists for:
    // he has just said the drive is gone and the body does not cooperate, and
    // this is where he is told it is not him.
    //
    // Names testosterone (the UMP hinge) but NOT "manopause" -- info-mechanism
    // owns that reveal. Copy is the source document verbatim.
    //
    // No image, per operator direction.
    //
    // CTA is pinned. Measured at 375x667: this article is 738px, so an in-flow
    // button would sit around the ~628 iPhone SE Safari fold rather than far
    // below it -- recorded honestly, because "five paragraphs push the CTA far
    // below the fold" is the intuitive justification and the measurement does
    // not support it. The pin is a small win here rather than the load-bearing
    // fix it is on info-mechanism (1223px article). Since 2026-08-21 the shared
    // bar carries 40px of mobile bottom clearance, so the button holds 579..627
    // -- fully clear of the 628 fold.
    id: "info-absolution",
    type: "info",
    heading: "You’re not imagining it.",
    body: [
      "You can still want your wife. Still be attracted to her. Still feel like the same guy upstairs…",
      "…and yet your body doesn’t always cooperate like it used to.",
      "That doesn’t automatically mean something is “wrong” with you, but it also doesn’t mean you have to chalk it up to getting older.",
      "Testosterone plays a role in sex drive, energy, mood, muscle, and body composition. And when those things start changing together, it may be worth looking at what’s happening beneath the surface.",
      "A few more questions and we’ll show you what your answers suggest.",
    ],
  },
  {
    // Options are KINDS of change, not a severity ladder, so the source order
    // is kept as authored. Each maps to an quiz-v6 body-composition bucket in
    // the deriver; "Not really" maps to `average`, never `lean-muscular`.
    id: "body-change",
    type: "single",
    prompt: "Have you noticed changes in your body over the last few years?",
    options: [
      { value: "belly-chest-jaw", label: "Bigger belly, chest fat, softer jawline" },
      { value: "less-muscle", label: "Less muscle than I used to have" },
      { value: "softer-overall", label: "Softer overall, even though my weight hasn’t changed much" },
      { value: "not-really", label: "Not really" },
    ],
  },
  {
    // Frequency companion to S1's magnitude question (both measure desire).
    // Kept deliberately -- frequency plus magnitude of one symptom is standard
    // instrument design -- but held to tier3 in the deriver so desire is not
    // counted twice at the top tier.
    id: "sex-thoughts",
    type: "single",
    prompt: "How often do you find yourself thinking about sex compared to 3–5 years ago?",
    options: [
      { value: "much-less", label: "Much less often" },
      { value: "somewhat-less", label: "Somewhat less often" },
      { value: "about-same", label: "About the same" },
      { value: "more-often", label: "More often" },
    ],
  },
  {
    // The coping side of S4 -- what he has already reached for. The single
    // strongest "already shopping for a pharmaceutical answer" signal in the
    // flow, which is why the classifier routes `almost-every-time` straight to
    // trt-alternative.
    id: "pill-reliance",
    type: "single",
    prompt: "How often do you feel like you need to “get yourself in the mood” or rely on “the little blue pill” before intimacy?",
    options: [
      { value: "almost-every-time", label: "Almost every time" },
      { value: "pretty-often", label: "Pretty often" },
      { value: "occasionally", label: "Occasionally" },
      { value: "rarely", label: "Rarely" },
    ],
  },
  {
    // Doubles as the flow's tolerance check (7-phase architecture, phase 4):
    // the only screen that asks what this COSTS rather than how often it
    // happens. Placed immediately before the onset question and the villain
    // reveal so peak pressure flows into them. Also sets the quality-of-life
    // floor in the deriver -- without it, `high` QoL is unreachable on every
    // path (see CONFIDENCE_TO_QOL_FLOOR).
    id: "confidence",
    type: "single",
    prompt: "Have you noticed changes in your confidence inside and outside of the bedroom?",
    options: [
      { value: "definitely", label: "Definitely" },
      { value: "somewhat", label: "Somewhat" },
      { value: "a-little", label: "A little" },
      { value: "not-at-all", label: "Not at all" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      // The source document set this in three paragraphs; `whyWeAsk.body` is a
      // single string rendered as one <p>, so they are joined. Two mechanical
      // fixes to the source: "if it’ll happen tonight whether she’ll notice"
      // was missing its sentence break, and "used to be…." had four dots.
      body: "When performance becomes unpredictable, it’s easy for the problem to move from your body into your head. You start wondering if it’ll happen tonight. Whether she’ll notice. Whether you’re still the same man you used to be… That mental pressure can make intimacy feel like a test instead of something you actually look forward to, and spill out into other areas of your life.",
    },
  },
  {
    // ignite-verbatim id AND values -- feeds the decline pattern and the
    // organic age-related-decline score untranslated.
    id: "when-started",
    type: "single",
    prompt: "When did you first notice these changes?",
    options: [
      { value: "over-year", label: "More than a year ago" },
      { value: "past-year", label: "Within the past year" },
      { value: "few-months", label: "Within the past few months" },
      { value: "not-sure", label: "I can’t remember" },
    ],
  },
  {
    // Villain reveal, and the first naming of "manopause" in the flow. Copy is
    // the source document verbatim, including the one-line rhetorical beats --
    // those are cadence, not stray paragraphs, so they are not merged.
    id: "info-mechanism",
    type: "info",
    heading: "These signs could point to more than “just getting older.”",
    body: [
      "Your answers suggest you may be experiencing several of the changes men commonly associate with manopause, including lower drive, less reliable performance, lower energy, and changes in body composition.",
      "And here’s the part most guys never get told:",
      "Testosterone isn’t just about muscle.",
      "It’s involved in your sex drive, energy, mood, and overall sense of vitality.",
      "As testosterone changes with age, the effects can show up in more places than the bedroom.",
      "The good news?",
      "Your age doesn’t automatically mean your best years are behind you.",
      "Let’s calculate your results and see what your answers reveal.",
    ],
    // The three phrases the source document set in bold, which is also a clean
    // scan path: villain -> reframe -> hope. The third runs 66 characters,
    // right at the limit the `bodyAccents` guidance sets -- it is kept because
    // it is a STANDALONE one-sentence paragraph, so orange-ing all of it reads
    // as a pull-line rather than as a highlighter smear inside a longer
    // paragraph, which is the case that guidance is warning about.
    bodyAccents: [
      "manopause",
      "Testosterone isn’t just about muscle.",
      "Your age doesn’t automatically mean your best years are behind you.",
    ],
    // Lifted from the body's own closing line rather than falling through to
    // the locale-pack "Continue". This is the peak-commitment beat and the
    // button is pinned, so a generic label wastes it.
    cta: "Calculate my results",
    // Eight paragraphs plus an image make this the flow's longest screen, and
    // the one the pinned CTA actually exists for. Measured at 375x667: the
    // article is 1223px and the button holds 579..627 at the top AND middle of
    // scroll (the signature of correct bottom-pinning, matching quiz-beer's
    // bridges), then rests at 515..563 at full scroll where the sticky
    // container ends -- fully visible at every position, with 40px of clearance
    // from the viewport edge since the 2026-08-21 StickyCta change. Note
    // `stickyCta` +
    // `image` is a legitimate pair since 2026-08-14; the article renders
    // body -> image with the button pinned.
    // Operator-supplied. The source document's placeholder asked for a
    // before/after comparison (tired man vs confident man); the operator
    // supplied this problem-state photograph instead, so the bridge closes on
    // the cost rather than on the transformation.
    //
    // Re-encoded from a 672x672 RGBA PNG whose rounded corners were baked into
    // the alpha channel: composited onto BLACK and flattened to yuv420p AVIF
    // (13 KB, crf 24 -- mean abs diff 2.43 vs the source). Two reasons not to
    // keep the alpha: libaom drops it outright, and the widget applies its own
    // `rounded-2xl`, so a baked ~32px radius would fight the 16px CSS one. The
    // black corners are invisible against the black surface -- verified by
    // rendering the screen, not assumed.
    //
    // 672 natural renders at 343 CSS px, i.e. exactly 2x on a DPR-2 phone, which
    // is the house reference (a first 336px export was retina-soft; the operator
    // re-exported). Replacing this file in place is safe ONLY while the quiz is
    // unpublished -- once it is live the CDN's max-age=86400 + 7d SWR means an
    // in-place swap never reaches returning users, and a NEW path is required.
    //
    // DELIBERATE: no product shot anywhere before the offer page. Cold-traffic
    // soft sell, same as quiz-sleep. Do not "fix" it by swapping in a bottle.
    image: {
      src: "/static/images/quiz-libido-mechanism.avif",
      alt: "A couple lying awake in bed at night, turned away from each other",
    },
  },
  {
    // Pays off info-mechanism's closing line ("Let’s calculate your results and
    // see what your answers reveal") and names the same five domains the
    // verdict is about to report on.
    id: "info-analyzing",
    type: "info",
    heading: "Analyzing your manopause profile…",
    body: [
      "Counting the signs you reported and analyzing the patterns across your libido, performance, energy, body composition, and confidence.",
      "Your results are almost ready…",
    ],
    animation: "analyzing",
    autoAdvanceMs: 5000,
  },
];

export const quizLibidoQuiz = defineQuiz({
  id: "quiz-libido",
  slug: "quiz-libido",
  experiment: MM_0102,
  meta: {
    // The source document's Alt 2. Used as the Shopify page title.
    title: "Is Manopause Killing Your Libido?",
    // The source document's Alt 1, chosen over Alt 2 as the H1 (operator
    // decision): it is curiosity-led and names no hormone, so the villain
    // reveal stays whole for info-mechanism. Short by design -- see the fold
    // note on S1.
    introHeadline: "Has something changed in the bedroom?",
    introAccent: "changed",
    // Compressed from the source document's intro HEADLINE + SUBTITLE ("Take
    // this 2 minute quiz to uncover what your body may be trying to tell you." /
    // "10 signs. 60 seconds. See how many you have in less than 2 minutes.").
    // Two edits: "60 seconds" is dropped because it contradicted the same
    // sentence's "less than 2 minutes", and the rest is cut to ONE rendered
    // line, which is worth 26px of fold on the screen that needs it most (see
    // the S1 measurement above). Both surviving elements are the ones that do
    // work -- the sign count the ads promise and the time-to-complete.
    introBody:
      "10 signs. 2 minutes. See how many you have.",
    testimonial: {
      // Source document verbatim. Angle-matched (getting older / something more
      // going on), reveal-safe (names no product or benefit category), says
      // "this quiz" rather than a bare "it", and unquantified.
      quote:
        "I thought I was just getting older. This quiz made me realize there was more going on.",
      // The document said "MARK, Verified Review". MARK is already the live
      // cortisol quiz's reviewer, so the display name is varied to avoid a
      // collision for anyone who sees both.
      // TODO(operator): back this with a real review. The "Verified Review"
      // suffix is deliberately absent until then -- but note QuizIntro renders
      // <VerifiedTick /> unconditionally, so a tick still shows.
      author: "TOM",
      // Set explicitly to avoid the shared default, which is a 3840x2560 /
      // 10.1 MB PNG rendered into a 48px circle (rule 19). This file is a byte
      // copy of quiz-glp-testimonial-avatar.webp (256x256, 12 KB), kept as a
      // per-quiz asset so each quiz's images stay self-contained.
      // TODO(operator): the same face is "DAVID R." on /pages/quiz-glp -- a
      // purpose-built avatar would remove the overlap.
      avatar: "/static/images/quiz-libido-testimonial-avatar.webp",
    },
    // MM-0102 -- see quizzes/_experiments.ts.
    introVariantTag: "control",
  },
  questions,

  branching(_answers: Answers, currentQuestionId: string): string | null {
    const idx = QUESTION_ORDER.indexOf(currentQuestionId as (typeof QUESTION_ORDER)[number]);
    if (idx < 0) return null;
    const next = QUESTION_ORDER[idx + 1];
    return next ?? null;
  },

  classifier(answers: Answers): PersonaKey {
    const age = answers["age"];
    const onset = answers["when-started"];
    const libido = answers["libido"];
    const morningWood = answers["morning-wood"];
    const reliability = answers["reliability"];
    const pill = answers["pill-reliance"];

    // Note: energy-day, body-change, sex-thoughts and confidence are not read
    // here -- they are persuasion / severity screens that feed the deriver
    // (quizzes/quiz-libido-profile.ts), not routing signals.
    //
    // `performance` is unreachable: it needs a lean body-composition signal and
    // this flow's body question has no lean option. `glp1-user` is unreachable
    // for the same reason as quiz-v6 / cortisol / quiz-sleep -- no GLP-1
    // screener.

    // TRT-alternative, two ways in. Either he is already reaching for a pill
    // most times he wants sex -- the clearest pharmaceutical-shopper signal the
    // flow can see -- or he reports a multi-year collapse across desire AND
    // physical function, which is the full EMAS cluster.
    if (pill === "almost-every-time") {
      return "trt-alternative";
    }
    if (
      onset === "over-year" &&
      libido === "significantly-lower" &&
      (reliability === "much-less-often" || morningWood === "cant-remember")
    ) {
      return "trt-alternative";
    }

    // Skeptical buyer: can’t place when it started.
    if (onset === "not-sure") {
      return "skeptical-buyer";
    }

    // Aging men: 40+ reporting a real drop in drive or morning erections.
    if (
      (age === "50-plus" || age === "40-49") &&
      (libido === "significantly-lower" ||
        libido === "somewhat-lower" ||
        morningWood === "cant-remember" ||
        morningWood === "rarely")
    ) {
      return "aging-men";
    }

    return "everyday-man";
  },

  deriveProfile: deriveLibidoProfile,
});
